// UserSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import 'react-toastify/dist/ReactToastify.css';
import { toast } from "react-toastify";
import apiClient from "../api/apiClient";
import { HealthCondition } from "../interfaces/HealthCondition";

interface UserState {
    loading: boolean;
    user: any | null;
    error: string | null;
    isLoggedIn: boolean;
    data: any | null;
}

interface UserCredentials {
    email: string;
    password: string;
}

const getInitialIsLoggedIn = (): boolean => {
    try {
        const isLoggedInStr = localStorage.getItem("isLoggedIn");
        const loginData = localStorage.getItem("loginData");
        return isLoggedInStr === "true" && !!loginData;
    } catch {
        return false;
    }
};

const getInitialUserData = () => {
    try {
        const loginData = localStorage.getItem("loginData");
        return loginData ? JSON.parse(loginData) : null;
    } catch {
        return null;
    }
};

const initialState: UserState = {
    loading: false,
    user: getInitialUserData(),
    error: null,
    isLoggedIn: getInitialIsLoggedIn(),
    data: null,
};

interface UserPayload {
  name?: string;
  fullName?: string;
  email: string;
  phoneNumber?: string;
  terms?: boolean;
  password: string;
  isAdmin?: boolean;
}

export type { HealthCondition };

export const fetchHealthData = createAsyncThunk('user/fetchHealthData', async (_, thunkAPI) => {
    try {
        const response = await apiClient.get('/api/v1/healthConditions/all');
        return response.data.data.healthConditions;
    } catch (error: any) {
        return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
});

export const fetchUserData = createAsyncThunk('user/fetchUserData', async (_, thunkAPI) => {
    try {
        const response = await apiClient.get('/api/v1/healthConditions');
        return response.data.data;
    } catch (error: any) {
        return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
});

export const loginUser = createAsyncThunk<HealthCondition, UserCredentials, { rejectValue: string }>(
    'user/loginUser',
    async (userCredentials: UserCredentials, thunkAPI) => {
        try {
            const response = await apiClient.post('/api/v1/auth/login', userCredentials);
            localStorage.setItem('loginData', JSON.stringify(response.data.data));
            localStorage.setItem('isLoggedIn', 'true');
            return response.data.data;
        } catch (error: any) {
            const msg = error.response?.data?.error?.message || error.message || 'Login failed';
            toast.error(msg);
            return thunkAPI.rejectWithValue(msg);       
        }
    }
);

export const registerUser = createAsyncThunk<
  boolean,
  UserPayload,
  { rejectValue: string }
>("user/registerUser", async (userPayload: UserPayload, thunkAPI) => {
  try {
    const response = await apiClient.post('/api/v1/auth/register', userPayload);
    localStorage.setItem("loginData", JSON.stringify(response.data));
    localStorage.setItem("isLoggedIn", "true");
    return response.data.status;
  } catch (error: any) {
    const msg = error.response?.data?.error?.message || error.message || 'Registration failed';
    toast.error(msg);
    return thunkAPI.rejectWithValue(msg);
  }
});

export const ChangeUserPassword = createAsyncThunk<
  boolean,
  { newPassword: string; password: string; confirmPassword: string },
  { rejectValue: string }
>("user/ChangeUserPassword", async (userPayload, thunkAPI) => {
  try {
    const response = await apiClient.post('/api/v1/patients/profile/reset-password', userPayload);  
    toast.success('Password reset successful');
    return response.data.status;
  } catch (error: any) {
    const msg = error.response?.data?.error?.message || error.message || 'Password reset failed';
    toast.error(msg);
    return thunkAPI.rejectWithValue(msg);
  }
});

export const logoutUsers = createAsyncThunk(
    'user/logoutUser',
    async (_, {rejectWithValue}) => {
        try {
            await apiClient.post('/api/v1/auth/logout', {});
        } catch (error: any) {
            return rejectWithValue(error.message);
        } finally {
            localStorage.removeItem('loginData');
            localStorage.removeItem('isLoggedIn');
            localStorage.removeItem('SetSlug');
            localStorage.removeItem('profile');
        }
    }
);

export const deleteHealthCondition = createAsyncThunk(
    'user/deleteHealthCondition',
    async (healthConditionId: string, {getState, rejectWithValue}) => {
        try {
            await apiClient.delete(`/api/v1/healthConditions/${healthConditionId}`);
            return healthConditionId;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        logoutUser: (state) => {
            state.isLoggedIn = false;
            state.user = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(registerUser.pending, (state) => {
          state.loading = true;
          state.user = null;
          state.error = null;
        })
        .addCase(registerUser.fulfilled, (state, action) => {
          state.loading = false;
          state.user = action.payload;
          state.error = null;
        })
        .addCase(registerUser.rejected, (state, action) => {
          state.loading = false;
          state.user = null;
          state.error = action.error?.message || 'An error occurred during registration.';
        })
        .addCase(ChangeUserPassword.pending, (state) => {
            state.loading = true;
            state.user = null;
            state.error = null;
          })
          .addCase(ChangeUserPassword.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload;
            state.error = null;
          })
          .addCase(ChangeUserPassword.rejected, (state, action) => {
            state.loading = false;
            state.user = null;
            state.error = action.error?.message || 'An error occurred during registration.';
          })
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
                state.user = null;
                state.error = null;
                state.isLoggedIn = false;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.error = null;
                state.isLoggedIn = true;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.user = null;
                state.isLoggedIn = false;
                if (action.error?.message === "Request failed with status code 422") {
                    state.error = 'Access Denied! Invalid Credentials';
                } else {
                    state.error = action.error?.message || 'An error occurred during login.';
                }
            }).addCase(fetchUserData.pending, (state) => {
            state.loading = true;
        })
            .addCase(fetchUserData.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchUserData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'An error occurred';
            }).addCase(deleteHealthCondition.pending, (state) => {
            state.loading = true;
        }).addCase(deleteHealthCondition.fulfilled, (state, action) => {
            state.loading = false;

            const deletedId = action.payload;
            if (Array.isArray(state.data)) {
                state.data = state.data.filter((healthCondition: any) => healthCondition.id !== deletedId);
            } else if (state.data && Array.isArray((state.data as any).healthConditions)) {
                (state.data as any).healthConditions = (state.data as any).healthConditions.filter(
                    (healthCondition: any) => healthCondition.id !== deletedId
                );
            }
        }).addCase(deleteHealthCondition.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        }).addCase(fetchHealthData.pending, (state) => {
            state.loading = true;
        })
            .addCase(fetchHealthData.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchHealthData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'An error occurred';
            })
            .addCase(logoutUsers.pending, (state) => {
                state.loading = true;
            })
            .addCase(logoutUsers.fulfilled, (state) => {
                state.loading = false;
                state.isLoggedIn = false;
                state.user = null;
                state.error = null;
                state.data = null;
            })
            .addCase(logoutUsers.rejected, (state) => {
                state.loading = false;
                state.isLoggedIn = false;
                state.user = null;
                state.error = null;
                state.data = null;
            });
    },
});

export const {logoutUser} = userSlice.actions;
export default userSlice.reducer;
